import React, { useState, useEffect } from "react";
import { db, auth } from "../firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { Lock, Rocket } from "lucide-react";

import MobileHeader from "./layout/MobileHeader";
import MobileBottomTabs from "./layout/MobileBottomTabs";
import MobileAccounts from "./pages/MobileAccounts"; 
import MobileAccountDashboard from "./pages/MobileAccountDashboard"; 
import MobileAddTrade from "./pages/MobileAddTrade";
import MobileTradeJournal from "./pages/MobileTradeJournal";
import MobileDrawer from "./layout/MobileDrawer";
import MobileSupport from "./pages/MobileSupport";
import Newsandevents from "./pages/MobileNewsandevents";
import LotCalculate from "./pages/MobileLotCalculate";

// IMPORT DESKTOP PAGES (Using names found in Dashboard.js)
import SubscriptionPlan from "../pages/SubscriptionPlan";

// GATEKEEP OVERLAY FOR MOBILE
const LockedOverlay = ({ setTab }) => (
  <div style={styles.lockOverlay}>
    <div style={styles.lockCard}>
      <div style={styles.lockIconCircle}>
        <Lock size={32} color="#38bdf8" />
      </div>
      <h2 style={styles.lockTitle}>Premium Feature</h2>
      <p style={styles.lockText}>
        This tool is exclusive to <b>Profitable Trader Plan</b> members.
      </p>
      <button onClick={() => setTab("subscription")} style={styles.unlockBtn}>
        <Rocket size={18} style={{ marginRight: 8 }} />
        Join the Family
      </button>
    </div>
  </div>
);

export default function MobileDashboard() {
  const [activeTab, setActiveTab] = useState("accounts");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [userData, setUserData] = useState({ plan: "free", expiry: null });

  // Real-time membership sync (same as web)
  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      const unsub = onSnapshot(doc(db, "users", user.uid), (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setUserData({
            plan: data?.plan || "free",
            expiry: data?.subscriptionExpiryDate || null
          });
        }
      });
      return () => unsub();
    }
  }, []);

  const checkMembershipStatus = () => {
    if (userData.plan !== "profitable") return false;
    if (!userData.expiry) return false;
    const now = new Date();
    const expiry = new Date(userData.expiry);
    return now < expiry;
  };

  const hasMembership = checkMembershipStatus();

  // Helper to wrap protected content
  const renderGated = (Component) => {
    return hasMembership ? Component : <LockedOverlay setTab={setActiveTab} />;
  };

  const handleTabChange = (tabId) => {
    if (tabId === "more") {
      setIsDrawerOpen(true);
    } else {
      setActiveTab(tabId);
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case "accounts":
        return <MobileAccounts />; 
      case "dashboard":
        return renderGated(<MobileAccountDashboard />);
      case "addTrade":
        return renderGated(<MobileAddTrade />);
      case "journal":
        return renderGated(<MobileTradeJournal />);
      case "support":
        return <MobileSupport />;
      case "subscription":
        return <SubscriptionPlan />;
      case "news":
        return renderGated(<Newsandevents />);
      case "lotCalculator": // ID used in Dashboard.js
        return renderGated(<LotCalculate />);
      default:
        return <MobileAccounts />;
    }
  };

  return (
    <div style={styles.appContainer}>
      <MobileHeader activeTab={activeTab} />
      
      <main style={styles.mainContent}>
        {renderContent()}
      </main>

      <MobileBottomTabs activeTab={activeTab} onChange={handleTabChange} />

      <MobileDrawer 
        isOpen={isDrawerOpen} 
        onClose={() => setIsDrawerOpen(false)} 
        activeTab={activeTab}
        setTab={setActiveTab}
      />

      <style>{`
        body, html {
          margin: 0;
          padding: 0;
          width: 100vw;
          overflow-x: hidden !important;
          background: #080b11;
        }
        main { width: 100%; overflow-x: hidden !important; }
      `}</style>
    </div>
  );
}

const styles = {
  appContainer: { display: "flex", flexDirection: "column", minHeight: "100vh", background: "#080b11" },
  mainContent: { flex: 1, width: "100%", paddingBottom: "110px" },
  // Mobile Gatekeep Styles
  lockOverlay: { padding: '40px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '70vh' },
  lockCard: { background: '#0f172a', padding: '32px 24px', borderRadius: '24px', border: '1px solid rgba(56, 189, 248, 0.2)', textAlign: 'center', width: '100%', boxShadow: '0 20px 40px rgba(0,0,0,0.4)' },
  lockIconCircle: { width: '60px', height: '60px', background: 'rgba(56, 189, 248, 0.1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' },
  lockTitle: { fontSize: '20px', fontWeight: '800', color: '#f8fafc', marginBottom: '12px' },
  lockText: { fontSize: '14px', color: '#94a3b8', lineHeight: '1.5', marginBottom: '24px' },
  unlockBtn: { width: '100%', background: '#38bdf8', color: '#020617', border: 'none', padding: '14px', borderRadius: '10px', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }
};